import { classNames } from "primereact/utils";
import type { CSSProperties, HTMLAttributes } from "react";

import { Loading } from "../loading";
import { StateMessage } from "./state-message";
import type { StateMessageRootAttributes } from "./types-state-message";
import {
  FORBIDDEN_DESCRIPTION,
  FORBIDDEN_TITLE,
  GENERIC_ERROR,
  LOADING_LABEL,
  RETRY_LABEL,
} from "./messages";
import styles from "./state-message.module.scss";

export interface ForbiddenStateProps extends Omit<StateMessageRootAttributes, "title"> {
  description?: string;
  inline?: boolean;
  testId?: string;
}

/** Acesso não autorizado com os textos padrão; `description` costuma ser a mensagem do serviço. */
export function ForbiddenState({
  description = FORBIDDEN_DESCRIPTION,
  inline,
  testId,
  ...rest
}: ForbiddenStateProps) {
  return (
    <StateMessage
      variant="forbidden"
      title={FORBIDDEN_TITLE}
      description={description}
      inline={inline}
      testId={testId}
      {...rest}
    />
  );
}
ForbiddenState.displayName = "ForbiddenState";

export interface ErrorStateProps extends Omit<StateMessageRootAttributes, "title"> {
  message?: string;
  description?: string;
  onRetry?: () => void;
  inline?: boolean;
  testId?: string;
}

/** Erro de carregamento; com `onRetry` mostra "Tentar novamente". `message` é o título. */
export function ErrorState({
  message = GENERIC_ERROR,
  description,
  onRetry,
  inline,
  testId,
  ...rest
}: ErrorStateProps) {
  return (
    <StateMessage
      variant="error"
      title={message}
      description={description}
      action={onRetry ? { label: RETRY_LABEL, onClick: onRetry, icon: "refresh" } : undefined}
      inline={inline}
      testId={testId}
      {...rest}
    />
  );
}
ErrorState.displayName = "ErrorState";

export interface LoadingStateProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  label?: string;
  size?: "sm" | "md";
  className?: string;
  style?: CSSProperties;
  testId?: string;
}

/** `Loading` do DS com texto; `role="status"` e `aria-live="polite"`. */
export function LoadingState({
  label = LOADING_LABEL,
  size = "md",
  className,
  style,
  testId,
  ...rest
}: LoadingStateProps) {
  return (
    <div
      className={classNames(styles.loading, className)}
      style={style}
      role="status"
      aria-live="polite"
      data-size={size}
      data-testid={testId}
      {...rest}
    >
      <Loading size={size} className={styles.spinner} aria-hidden />
      <span className={styles.loadingLabel}>{label}</span>
    </div>
  );
}
LoadingState.displayName = "LoadingState";
